import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { generateCompetitiveComparison } from '../../lib/openai';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiBarChart2, FiLoader, FiCopy, FiCheck } = FiIcons;

function CompetitiveComparison({ vehicle }) {
  const [comparison, setComparison] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [competitors, setCompetitors] = useState('');
  const [copied, setCopied] = useState(false);

  const generateComparison = async () => {
    if (!competitors.trim()) return;
    setIsLoading(true);
    try {
      const vehicleDetails = `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.type}`;
      const result = await generateCompetitiveComparison(vehicleDetails, competitors);
      setComparison(result);
    } catch (error) {
      console.error('Error generating comparison:', error);
      setComparison('Failed to generate comparison. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(comparison);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <SafeIcon icon={FiBarChart2} className="w-5 h-5 mr-2 text-primary-500" />
        Competitive Comparison
      </h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Competitor Vehicles
          </label>
          <textarea
            value={competitors}
            onChange={(e) => setCompetitors(e.target.value)}
            placeholder="E.g., Toyota RAV4, Honda CR-V, Mazda CX-5"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            rows={2}
          />
          <p className="text-xs text-gray-500 mt-1">Enter competing vehicles separated by commas</p>
        </div>

        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={generateComparison}
          disabled={isLoading || !competitors.trim()}
          className="w-full bg-primary-600 text-white py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
              <span>Generate Comparison</span>
            </>
          )}
        </motion.button>

        {comparison && (
          <div className="mt-4 border border-gray-200 rounded-lg p-4 bg-gray-50 relative">
            <pre className="whitespace-pre-wrap text-sm font-sans">{comparison}</pre>
            <button
              onClick={copyToClipboard}
              className="absolute top-2 right-2 p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 rounded-full transition-colors"
              title="Copy to clipboard"
            >
              <SafeIcon
                icon={copied ? FiCheck : FiCopy}
                className={`w-4 h-4 ${copied ? 'text-green-500' : ''}`}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CompetitiveComparison;